import React from "react";
import { Box, Fab, SxProps, Tooltip } from "@mui/material";
import {
  AddOutlined,
  CenterFocusWeakOutlined,
  LayersOutlined,
  LocationOnOutlined,
  MyLocationOutlined,
  RemoveOutlined
} from "@mui/icons-material";
import ThreeDRotationIcon from "@mui/icons-material/ThreeDRotation";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

interface MapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onHomeClick?: () => void;
  on3DToggle?: () => void;
  onLocationRequest?: () => void;
  onLayerToggle?: () => void;
  isTrackingLocation?: boolean;
  isGettingLocation?: boolean;
  isOrbiting?: boolean;
  toggleOrbit?: () => void;
  sx?: SxProps;
}

const MapControls: React.FC<MapControlsProps> = ({
  onHomeClick,
  on3DToggle,
  onLocationRequest,
  onLayerToggle,
  onZoomIn,
  onZoomOut,
  isTrackingLocation,
  isGettingLocation,
  isOrbiting,
  toggleOrbit,
  sx
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", ...sx }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        gap: "0.625rem"
      }}
    >
      {onHomeClick && (
        <Tooltip title='Zoom to extent' placement='left'>
          <Fab
            aria-label='Zoom to extent'
            color='primary'
            onClick={onHomeClick}
            size='small'
            sx={{ backgroundColor: "#1D2939", borderRadius: "0.5rem", border: "1px solid rgba(252, 252, 253, 0.15)" }}
          >
            <CenterFocusWeakOutlined
              sx={{
                height: "1.25rem",
                width: "1.25rem"
              }}
            />
          </Fab>
        </Tooltip>
      )}

      {toggleOrbit && (
        <Tooltip title={isOrbiting ? "Stop orbiting" : "Start orbiting"}>
          <Fab
            color='primary'
            aria-label='Orbit view'
            onClick={toggleOrbit}
            size='small'
            sx={{ backgroundColor: "#1D2939", borderRadius: "0.5rem", border: "1px solid rgba(252, 252, 253, 0.15)" }}
          >
            {isOrbiting ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
          </Fab>
        </Tooltip>
      )}

      {on3DToggle && (
        <Tooltip title='Toggle 3D view' placement='left'>
          <Fab
            color='primary'
            aria-label='Toggle 3D view'
            onClick={on3DToggle}
            size='small'
            sx={{ backgroundColor: "#1D2939", borderRadius: "0.5rem", border: "1px solid rgba(252, 252, 253, 0.15)" }}
          >
            <ThreeDRotationIcon
              sx={{
                height: "1.25rem",
                width: "1.25rem"
              }}
            />
          </Fab>
        </Tooltip>
      )}

      {onLocationRequest && (
        <Tooltip title={isTrackingLocation ? "Go to location & stop tracking" : "Find & track my location"} placement='left'>
          <Fab
            color={isTrackingLocation ? "error" : "primary"}
            aria-label='My location'
            onClick={onLocationRequest}
            size='small'
            disabled={isGettingLocation}
            sx={{
              backgroundColor: "#1D2939",
              borderRadius: "0.5rem",
              border: "1px solid rgba(252, 252, 253, 0.15)",
              ...(isGettingLocation && {
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%": {
                    transform: "scale(1)"
                  },
                  "50%": {
                    transform: "scale(1.05)"
                  },
                  "100%": {
                    transform: "scale(1)"
                  }
                }
              }),
              ...(isTrackingLocation && {
                animation: "tracking 2s ease-in-out infinite",
                "@keyframes tracking": {
                  "0%": {
                    boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.4)"
                  },
                  "70%": {
                    boxShadow: "0 0 0 10px rgba(244, 67, 54, 0)"
                  },
                  "100%": {
                    boxShadow: "0 0 0 0 rgba(244, 67, 54, 0)"
                  }
                }
              })
            }}
          >
            {isTrackingLocation ? (
              <LocationOnOutlined
                sx={{
                  height: "1.25rem",
                  width: "1.25rem"
                }}
              />
            ) : (
              <MyLocationOutlined
                sx={{
                  height: "1.25rem",
                  width: "1.25rem"
                }}
              />
            )}
          </Fab>
        </Tooltip>
      )}

      {onLayerToggle && (
        <Tooltip title='Layers' placement='left'>
          <Fab
            color='primary'
            aria-label='layers'
            onClick={onLayerToggle}
            size='small'
            sx={{ backgroundColor: "#1D2939", borderRadius: "0.5rem", border: "1px solid rgba(252, 252, 253, 0.15)" }}
          >
            <LayersOutlined
              sx={{
                height: "1.25rem",
                width: "1.25rem"
              }}
            />
          </Fab>
        </Tooltip>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0
        }}
      >
        <Tooltip title='Zoom in' placement='left'>
          <Fab
            color='primary'
            aria-label='Zoom in'
            onClick={onZoomIn}
            size='small'
            sx={{
              backgroundColor: "#1D2939",
              borderTopLeftRadius: "0.5rem",
              borderTopRightRadius: "0.5rem",
              borderBottomLeftRadius: "0",
              borderBottomRightRadius: "0",
              border: "1px solid rgba(252, 252, 253, 0.15)",
              color: "white" // Ensure icon is white for visibility on dark bg
            }}
          >
            <AddOutlined />
          </Fab>
        </Tooltip>

        <Tooltip title='Zoom out' placement='left'>
          <Fab
            color='primary'
            aria-label='Zoom out'
            onClick={onZoomOut}
            size='small'
            sx={{
              backgroundColor: "#1D2939",
              borderRadius: "0.5rem",
              border: "1px solid rgba(252, 252, 253, 0.15)",
              color: "white",
              borderTopLeftRadius: "0",
              borderTopRightRadius: "0",
              borderBottomLeftRadius: "0.5rem",
              borderBottomRightRadius: "0.5rem"
            }}
          >
            <RemoveOutlined
              sx={{
                height: "1.25rem",
                width: "1.25rem"
              }}
            />
          </Fab>
        </Tooltip>
      </Box>
    </Box>
  </Box>
);

export default React.memo(MapControls);
